import React, { useState } from 'react';
import {
  Upload, Button, UploadProps, App,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/es/upload/interface';
import { attachmentApi } from 'entities/attachment/api/attachmentApi';
import { useGlobalSpinner } from 'shared/hooks';

export interface FileUploadProps {
  /** Тип файла для API (например, 'nameplate', 'document') */
  fileType?: string;
  /** Максимальное количество файлов */
  maxCount?: number;
  /** Показывать ли список загруженных файлов */
  showUploadList?: UploadProps['showUploadList'];
  /** Принимаемые типы файлов */
  accept?: string;
  /** Текст кнопки */
  buttonText?: string;
  /** Размер кнопки */
  buttonSize?: 'small' | 'middle' | 'large';
  /** Функция обратного вызова при успешной загрузке файла */
  onFileUploaded?: (uuid: string, file: File) => void;
  /** Функция обратного вызова при удалении файла */
  onFileRemoved?: (uuid: string) => void;
  /** Список загруженных файлов (UUID) */
  uploadedFiles?: string[];
  /** Дополнительные пропсы для Upload */
  uploadProps?: Omit<UploadProps, 'beforeUpload' | 'maxCount' | 'showUploadList' | 'fileList' | 'accept' | 'onRemove'>;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  fileType = 'default',
  maxCount = 1,
  showUploadList = { showRemoveIcon: true },
  accept,
  buttonText = 'Загрузить файл',
  buttonSize = 'small',
  onFileUploaded,
  onFileRemoved,
  uploadedFiles,
  uploadProps,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const { showSpinner, hideSpinner } = useGlobalSpinner();
  const { message } = App.useApp();

  // Определяем режим управления списком файлов
  const isControlled = Array.isArray(uploadedFiles);
  const displayedFileList: UploadFile[] = React.useMemo(() => {
    if (isControlled) {
      const uuids = uploadedFiles as string[];
      return uuids.map((uuid, index) => ({
        uid: uuid,
        name: `Файл ${index + 1}`,
        status: 'done' as const,
        url: undefined as string | undefined,
      }));
    }
    return fileList;
  }, [isControlled, uploadedFiles, fileList]);

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      showSpinner('Загрузка файла...');

      // Загружаем файл через API
      const response = await attachmentApi.uploadDraft(fileType, file);

      // Предполагаем, что API возвращает массив объектов с uuid
      const uuid = response?.[0]?.fileUuid ?? (typeof response?.[0]?.id === 'number' ? String(response[0].id) : undefined);

      if (typeof uuid === 'string') {
        // В контролируемом режиме полагаемся на родителя
        if (!isControlled) {
          const newFile: UploadFile = {
            uid: uuid,
            name: file.name,
            status: 'done',
            url: undefined,
          };
          setFileList((prev) => [...prev, newFile]);
        }

        // Вызываем callback с UUID в следующем тике
        setTimeout(() => {
          onFileUploaded?.(uuid, file);
          message.success('Файл успешно загружен');
        }, 0);
      } else {
        throw new Error('Не удалось получить UUID файла');
      }
    } catch (error) {
      console.error('Ошибка загрузки файла:', error);
      message.error('Ошибка загрузки файла');
    } finally {
      setUploading(false);
      hideSpinner();
    }

    return false; // Предотвращаем автоматическую загрузку
  };

  const handleRemove = async (file: UploadFile) => {
    try {
      if (file.uid) {
        showSpinner('Удаление файла...');
        // Удаляем файл через API
        await attachmentApi.delete(file.uid);

        // Удаляем из списка (в неконтролируемом режиме)
        if (!isControlled) {
          setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
        }
        // Вызываем callback с UUID
        onFileRemoved?.(file.uid);

        message.success('Файл удален');
      }
    } catch (error) {
      console.error('Ошибка удаления файла:', error);
      message.error('Ошибка удаления файла');
    } finally {
      hideSpinner();
    }
  };

  return (
    <Upload
      beforeUpload={handleUpload}
      maxCount={maxCount}
      showUploadList={{
        showRemoveIcon: true,
        showDownloadIcon: false,
        ...(typeof showUploadList === 'object' ? showUploadList : {}),
      }}
      fileList={displayedFileList}
      accept={accept}
      onRemove={handleRemove}
      disabled={uploading}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...uploadProps}
    >
      <Button size={buttonSize} icon={<UploadOutlined />} loading={uploading}>
        {buttonText}
      </Button>
    </Upload>
  );
};
